import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { type ReactNode, useState } from "react";
import type { AccessResponse } from "../../../server/routes/companies";
import { allows, optionsOf, type PermissionValues, setTo, type ToolPermission, USE_PERMISSION, valuesForLevel } from "../../../shared/tools";
import { comingSoonTools } from "../../../tools/web";
import { ErrorNotice, Notice, Panel, Spinner } from "../../components/ui";
import { api } from "../../lib/api";
import { useCompany } from "../../lib/company";

/*
 * What each ServiceM8 security role can do: manage the app, and in each tool, permission by
 * permission (like ServiceM8's own "Modify Security Role": tabs per tool, a dropdown per thing).
 * ServiceM8 doesn't tell apps what a role allows, so managers choose here; until a role's are
 * changed, they start from a guess from its name (managers everything, "no pricing" roles no Price List, the rest view only).
 */

type Tool = AccessResponse["tools"][number];
type Role = AccessResponse["roles"][number];

/** A role's settings in a tool, in a word or two: Full access, View only, No access, or Custom. */
function summary(tool: Tool, values: PermissionValues) {
    const optionsOfAll = tool.permissions.map((p) => ({ p, options: optionsOf(p).map((o) => o.value) }));
    if (values[USE_PERMISSION] === optionsOfAll.find((x) => x.p.id === USE_PERMISSION)?.options.at(-1)) return "No access";
    if (optionsOfAll.every(({ p, options }) => values[p.id] === options[0])) return "Full access";
    const view = valuesForLevel({ name: tool.name, permissions: tool.permissions }, "view");
    if (tool.permissions.every((p) => values[p.id] === view[p.id])) return "View only";
    return "Custom";
}

export function AccessSection() {
    const company = useCompany();
    const base = `/c/${company.id}`;
    const qc = useQueryClient();
    const key = ["c", company.id, "admin", "access"];
    const access = useQuery({ queryKey: key, queryFn: () => api<AccessResponse>(`${base}/access`) });
    const setManager = useMutation({
        mutationFn: (v: { securityRoleUuid: string; manager: boolean }) => api(`${base}/managers`, { method: "PUT", body: v }),
    });
    const [showEmpty, setShowEmpty] = useState(false);
    // The role being changed (its permissions shown below the table), and its tool tab.
    const [open, setOpen] = useState<{ role: string; tool: string } | null>(null);
    const data = access.data;
    const used = data?.roles.filter((r) => r.staffCount > 0) ?? [];
    const empty = data?.roles.filter((r) => r.staffCount === 0) ?? [];
    const roles = showEmpty ? [...used, ...empty] : used;
    const emptyRoles = empty.length === 1 ? "role" : "roles";
    const managerHelp =
        "Managers can change settings and control syncing. The ServiceM8 account holder, and whoever connected ServiceM8, are always managers.";
    const openRole = open ? data?.roles.find((r) => r.uuid === open.role) : undefined;

    return (
        <div className="space-y-6">
            <Panel title="Access" flush>
                <p className="px-5 pt-4 text-muted">
                    What each ServiceM8 security role can do here. Change someone&apos;s role in ServiceM8 and their access follows. Changes to ServiceM8 data
                    are still checked against their own ServiceM8 permissions.
                </p>
                {!data && <div className="p-4">{access.error ? <ErrorNotice error={access.error} /> : <Spinner />}</div>}
                {data?.available === false && (
                    <div className="p-[14px]">
                        <Notice>Security roles haven&apos;t synced yet, so only managers can make changes for now.</Notice>
                    </div>
                )}
                {data?.available === true && (
                    <>
                        <table className="list mt-2">
                            <thead>
                                <tr>
                                    <th>ServiceM8 role</th>
                                    <th className="text-center" title={managerHelp}>
                                        Manager
                                    </th>
                                    {data.tools.map((t) => (
                                        <th key={t.id}>{t.name}</th>
                                    ))}
                                    {comingSoonTools.map((t) => (
                                        <th key={t.id}>{t.name}</th>
                                    ))}
                                    <th>
                                        <span className="sr-only">Change</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map((role) => {
                                    // Your own role stays a manager (locked on), so you can't lock yourself out.
                                    const ownRole = role.uuid === data.myRoleUuid && role.manager.manager;
                                    return (
                                        <tr key={role.uuid} className={clsx(role.staffCount === 0 && "text-muted", open?.role === role.uuid && "bg-brand-500/10")}>
                                            <td>
                                                <span className="font-semibold" title={role.description || undefined}>
                                                    {role.name}
                                                </span>
                                                <span className="ml-2 text-xs whitespace-nowrap text-muted">
                                                    {role.staffCount} {role.staffCount === 1 ? "person" : "people"}
                                                </span>
                                                {role.uuid === data.myRoleUuid && <span className="ml-2 text-xs text-brand-ink">your role</span>}
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className={clsx("size-4 align-middle", !role.manager.isDefault && "outline-2 outline-offset-2 outline-brand-500")}
                                                    aria-label={`${role.name} is a manager`}
                                                    title={ownRole ? "Your role: always a manager, so you can't lock yourself out." : managerHelp}
                                                    checked={role.manager.manager}
                                                    disabled={setManager.isPending || ownRole}
                                                    onChange={(e) => setManager.mutate({ securityRoleUuid: role.uuid, manager: e.target.checked })}
                                                />
                                            </td>
                                            {data.tools.map((t) => (
                                                <td key={t.id}>{summary(t, role.tools[t.id]?.values ?? {})}</td>
                                            ))}
                                            {comingSoonTools.map((t) => (
                                                <td key={t.id} className="text-muted">
                                                    Coming soon
                                                </td>
                                            ))}
                                            {/* One per role: its permissions, every tool, open below. */}
                                            <td className="text-right">
                                                <button
                                                    type="button"
                                                    className="btn-secondary px-2.5 py-1 text-xs"
                                                    aria-label={`Change what ${role.name} can do`}
                                                    onClick={() => setOpen({ role: role.uuid, tool: open?.tool ?? data.tools[0]?.id ?? "" })}
                                                >
                                                    Change
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 text-xs text-muted">
                            {empty.length > 0 && (
                                <button type="button" className="link-button text-xs" onClick={() => setShowEmpty((v) => !v)}>
                                    {showEmpty ? "Hide roles nobody has" : `Show ${empty.length} ${emptyRoles} nobody has`}
                                </button>
                            )}
                        </div>
                        <div className="px-5 pb-3 empty:hidden">
                            <ErrorNotice error={setManager.error} />
                        </div>
                    </>
                )}
            </Panel>
            {data && openRole && open ? (
                <RolePermissions
                    role={openRole}
                    tools={data.tools}
                    tool={open.tool}
                    onTool={(tool) => setOpen({ role: openRole.uuid, tool })}
                    onClose={() => setOpen(null)}
                    onSave={(toolId, permission, value) =>
                        api(`${base}/access`, { method: "PUT", body: { toolId, securityRoleUuid: openRole.uuid, permission, value } }).finally(
                            () => void qc.invalidateQueries({ queryKey: key }),
                        )
                    }
                />
            ) : null}
        </div>
    );
}

/** A permission set on that needs another that isn't (e.g. Manage pricing needs See costs): it does nothing until that's on. */
function NeedsNotice({ tool, permission, values }: Readonly<{ tool: Tool; permission: ToolPermission; values: PermissionValues }>) {
    const needs = permission.requires ? tool.permissions.find((p) => p.id === permission.requires) : undefined;
    if (!needs || !setTo(tool, values, permission.id) || allows(tool, values, permission.id)) return null;
    return <span className="block text-xs text-warning">Needs &quot;{needs.label}&quot;, which is off, so it does nothing for now.</span>;
}

/** One role's permissions, like ServiceM8's "Modify Security Role": a tab per tool, its permissions in groups, a dropdown each (saved as chosen). */
function RolePermissions(
    props: Readonly<{
        role: Role;
        tools: readonly Tool[];
        tool: string;
        onTool: (tool: string) => void;
        onClose: () => void;
        onSave: (toolId: string, permission: string, value: string) => Promise<unknown>;
    }>,
) {
    const soon = comingSoonTools.find((t) => t.id === props.tool);
    const tool = props.tools.find((t) => t.id === props.tool) ?? props.tools[0];
    const [error, setError] = useState<Error | null>(null);
    const [saving, setSaving] = useState<string | null>(null);
    if (!tool) return null;
    const tabs = (
        <nav className="tabs bg-transparent" aria-label="Tools">
            {[...props.tools, ...comingSoonTools].map((t) => (
                <button key={t.id} type="button" className="tab cursor-pointer" aria-current={t.id === (soon ?? tool).id ? "page" : undefined} onClick={() => props.onTool(t.id)}>
                    {t.name}
                </button>
            ))}
        </nav>
    );
    const panel = (body: ReactNode) => (
        <Panel
            title={`Modify ${props.role.name}`}
            flush
            actions={
                <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={props.onClose}>
                    Done
                </button>
            }
        >
            {tabs}
            {body}
        </Panel>
    );
    // A tool on the way: nothing to set yet.
    if (soon) return panel(<p className="px-5 py-4 text-sm text-muted">{soon.name} is coming soon. What each role can do in it will be set here.</p>);
    const settings = props.role.tools[tool.id];
    const groups = Map.groupBy(tool.permissions, (p) => p.group);
    const save = (p: ToolPermission, value: string) => {
        setError(null);
        setSaving(p.id);
        props
            .onSave(tool.id, p.id, value)
            .catch((err: Error) => setError(err))
            .finally(() => setSaving(null));
    };
    return panel(
        <>
            {settings?.changed ? null : (
                <p className="px-5 pt-3 text-xs text-muted">Not changed yet: these follow this role&apos;s access before permissions. Change any to set them.</p>
            )}
            <div className="px-5 pb-4">
                {[...groups].map(([group, permissions]) => (
                    <section key={group} className="pt-4">
                        <h3 className="border-b border-line pb-1 text-sm font-medium text-muted">{group}</h3>
                        <ul className="m-0 list-none p-0">
                            {permissions.map((p) => (
                                <li key={p.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-line py-2 last:border-b-0">
                                    <span className="min-w-0 flex-1">
                                        <span className="block">{p.label}</span>
                                        <span className="block text-xs text-muted">{p.description}</span>
                                        <NeedsNotice tool={tool} permission={p} values={settings?.values ?? {}} />
                                    </span>
                                    <select
                                        className="input w-44 py-1 text-sm"
                                        aria-label={`${props.role.name}: ${p.label}`}
                                        value={settings?.values[p.id] ?? ""}
                                        disabled={saving !== null}
                                        onChange={(e) => save(p, e.target.value)}
                                    >
                                        {optionsOf(p).map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
                <div className="pt-3 empty:hidden">
                    <ErrorNotice error={error} />
                </div>
            </div>
        </>,
    );
}
